import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api/client';
import Text from '../Text/Text';
import ActionButton from '../ActionButton/ActionButton';
import './PaymentProcessing.css';
import { PaymentMethod } from '../../types';

// Define payment status types
type PaymentStatus = 'pending' | 'completed' | 'failed';

// Define payment details interface
interface PaymentDetails {
  status: PaymentStatus;
  transactionId?: string;
  totalAmount?: number;
  originalAmount?: number;
  platformFee?: number;
  sellerId?: string;
  userId?: string;
  itemDescription?: string;
  paymentMethod?: string | PaymentMethod;
  errorCode?: string;
  errorMessage?: string;
}

const PaymentProcessing: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number>(1000); // Default 1 second for faster updates with Hosted Fields
  
  // Extract reference from URL parameters or query parameters
  const getReference = () => {
    // First check if we have it in the URL path (this will be the case with Hosted Fields)
    if (reference) {
      return reference;
    }
    
    // Then check query parameters (this is for backward compatibility with redirect flow)
    const queryParams = new URLSearchParams(location.search);
    const fieldValue = queryParams.get('field1value');
    if (fieldValue) {
      return fieldValue;
    }
    
    // Finally check for reference parameter
    return queryParams.get('reference');
  };
  
  // Function to check payment status
  // With Hosted Fields, the payment status should be updated immediately or very quickly
  // since we're processing the payment directly in the backend rather than waiting for webhooks
  const checkPaymentStatus = async () => {
    try {
      const paymentReference = getReference();
      
      if (!paymentReference) {
        setError('Payment reference not found');
        setLoading(false);
        return;
      }
      
      const response = await apiClient.payments.getPaymentStatus(paymentReference);
      setPaymentDetails(response);
      
      if (response.status !== 'pending') {
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      setError(error.response?.data?.message || 'An error occurred while checking payment status');
      setLoading(false);
    }
  };
  
  // Start polling when component mounts
  useEffect(() => {
    // Check status immediately
    checkPaymentStatus();
    
    // Set up polling interval
    const intervalId = setInterval(() => {
      if (paymentDetails?.status === 'pending') {
        checkPaymentStatus();
      }
    }, pollingInterval);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [pollingInterval, paymentDetails?.status]);
  
  // Handle "Try Again" button click
  const handleTryAgain = () => {
    // Store payment details in session storage for pre-populating the form
    if (paymentDetails) {
      sessionStorage.setItem('veripay_payment_retry', JSON.stringify({
        amount: paymentDetails.totalAmount,
        sellerId: paymentDetails.sellerId,
        userId: paymentDetails.userId,
        itemId: paymentDetails.itemDescription
      }));
    }
    
    // Navigate back to payment form
    navigate('/pay');
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="payment-processing-container">
        <div className="payment-processing-content">
          <div className="loading-spinner"></div>
          <Text className="text-h2">Processing Payment</Text>
          <Text className="text-body">
            Please wait while we process your payment. This may take a few moments.
          </Text>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="payment-processing-container">
        <div className="payment-processing-content error">
          <Text className="text-h2">Something went wrong</Text>
          <Text className="text-body">{error}</Text>
          <ActionButton
            icon={<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>}
            label="Return to Payment Form"
            onClick={() => navigate('/pay')}
          />
        </div>
      </div>
    );
  }
  
  // Render failed payment state
  if (paymentDetails?.status === 'failed') {
    return (
      <div className="payment-processing-container">
        <div className="payment-processing-content error">
          <Text className="text-h2">Payment Unsuccessful</Text>
          <Text className="text-body">
            {paymentDetails.errorMessage || 'Your payment could not be processed.'}
          </Text>
          
          {/* Troubleshooting suggestions based on error type */}
          {paymentDetails.errorCode && (
            <div className="troubleshooting-tips">
              <Text className="text-caption">Troubleshooting Tips:</Text>
              {paymentDetails.errorCode === 'CARD_DECLINED' && (
                <Text className="text-body">
                  Your card was declined. Please check your card details or try a different payment method.
                </Text>
              )}
              {paymentDetails.errorCode === 'INSUFFICIENT_FUNDS' && (
                <Text className="text-body">
                  Your card has insufficient funds. Please try a different card or payment method.
                </Text>
              )}
              {paymentDetails.errorCode === 'EXPIRED_CARD' && (
                <Text className="text-body">
                  Your card has expired. Please update your card details or try a different payment method.
                </Text>
              )}
            </div>
          )}
          
          <ActionButton
            icon={<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
              <path d="M23 4v6h-6M1 20v-6h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>}
            label="Try Again"
            onClick={handleTryAgain}
          />
        </div>
      </div>
    );
  }
  
  // Render successful payment state
  if (paymentDetails?.status === 'completed') {
    return (
      <div className="payment-processing-container">
        <div className="payment-processing-content success">
          <Text className="text-h2">Payment Successful!</Text>
          
          <div className="transaction-details">
            <div>
              <Text className="text-body">
                <strong>Original Amount:</strong> ${paymentDetails.originalAmount?.toFixed(2)}
              </Text>
            </div>
            <div>
              <Text className="text-body">
                <strong>Platform Fee:</strong> ${paymentDetails.platformFee?.toFixed(2)}
              </Text>
            </div>
            <div>
              <Text className="text-body">
                <strong>Total Amount:</strong> ${paymentDetails.totalAmount?.toFixed(2)}
              </Text>
            </div>
            <div>
              <Text className="text-body">
                <strong>Item:</strong> {paymentDetails.itemDescription}
              </Text>
            </div>
            <div>
              <Text className="text-body">
                <strong>Seller ID:</strong> {paymentDetails.userId}
              </Text>
            </div>
            <div>
              <Text className="text-body">
                <strong>Payment Method:</strong> {
                  typeof paymentDetails.paymentMethod === 'string'
                    ? paymentDetails.paymentMethod
                    : paymentDetails.paymentMethod?.paymentType || 'Unknown'
                }
                {typeof paymentDetails.paymentMethod !== 'string' && paymentDetails.paymentMethod?.lastFour &&
                  ` ending ${paymentDetails.paymentMethod.lastFour}`
                }
              </Text>
            </div>
            <Text className="text-body">
              <div><strong>Transaction ID:</strong></div>
              <div>{paymentDetails.transactionId}</div>
            </Text>
          </div>
          
          <div className="next-steps-timeline">
            <Text className="text-h3">What happens next?</Text>
            
            <div className="timeline-item">
              <div className="timeline-icon">1</div>
              <div className="timeline-content">
                <div>
                  <Text className="text-body-bold">Seller Notification</Text>
                </div>
                <Text className="text-body">
                  The seller has been notified and must ship your item within 48 hours or your payment will be automatically refunded.
                </Text>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-icon">2</div>
              <div className="timeline-content">
                <div>
                  <Text className="text-body-bold">Shipping</Text>
                </div>
                <Text className="text-body">
                  The seller will provide Australia Post tracking information once shipped.
                </Text>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-icon">3</div>
              <div className="timeline-content">
                <div>
                  <Text className="text-body-bold">Tracking</Text>
                </div>
                <Text className="text-body">
                  VeriPay automatically monitors your package's journey.
                </Text>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-icon">4</div>
              <div className="timeline-content">
                <div>
                  <Text className="text-body-bold">Delivery</Text>
                </div>
                <Text className="text-body">
                  Once your package arrives or is ready for collection, you can release payment immediately or wait for automatic release after 48 hours.
                </Text>
              </div>
            </div>
          </div>
          
          <ActionButton
            icon={<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="9" y1="21" x2="9" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>}
            label="View My Transactions"
            onClick={() => navigate('/transactions')}
          />
        </div>
      </div>
    );
  }
  
  // Fallback render
  return (
    <div className="payment-processing-container">
      <div className="payment-processing-content">
        <Text className="text-h2">Payment Status Unknown</Text>
        <Text className="text-body">
          We couldn't determine the status of your payment. Please check your transactions or contact support.
        </Text>
        <ActionButton
          icon={<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="9" y1="21" x2="9" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>}
          label="View My Transactions"
          onClick={() => navigate('/transactions')}
        />
      </div>
    </div>
  );
};

export default PaymentProcessing;